import React, { useState } from 'react';
import { AlertTriangle, Clock, Users } from 'lucide-react';
import { useAuth } from '../packages/auth-client';
import { getErrorMessage } from '../packages/api-client';
import {
    DELETE_CONFIRMATION,
    DELETION_GRACE_DAYS,
    formatDeletionDate,
    isOwnerMustTransfer,
    requestAccountDeletion,
} from '../packages/api-client/account-deletion-api';
import { Button } from '../packages/ui';

/**
 * The delete-account flow, shared by the Profile tab and the standalone
 * /delete-account route (Play requires a deletion path usable by someone who
 * has already uninstalled the app, so the same panel has to work signed in on
 * a page that is not behind the app shell).
 *
 * Three terminal states: scheduled, owner-must-transfer, and error. The middle
 * one is deliberately NOT an error — the request was understood and refused
 * for a reason the user can act on.
 */

/** Where the sign-in screen picks up the date after the sign-out. */
export const DELETION_NOTICE_KEY = 'accountDeletionScheduledAt';

type Outcome =
    | { kind: 'idle' }
    | { kind: 'scheduled'; at: string }
    | { kind: 'owner_must_transfer' };

export function DeleteAccountPanel() {
    const { logout } = useAuth();
    const [confirmation, setConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });

    // The destructive action is enabled only once the exact word is typed. A
    // confirm dialog is one click from firing; typing is the part that cannot
    // happen by accident. Case and stray whitespace are forgiven, nothing else.
    const canSubmit =
        !submitting && confirmation.trim().toLowerCase() === DELETE_CONFIRMATION;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError('');
        try {
            const { scheduled_deletion_at } = await requestAccountDeletion();
            setOutcome({ kind: 'scheduled', at: scheduled_deletion_at });
            // Stash the date before signing out: on the Profile tab the
            // sign-out redirects to /login and unmounts this panel, and the
            // date is the one thing the user needs to keep.
            try {
                sessionStorage.setItem(DELETION_NOTICE_KEY, scheduled_deletion_at);
            } catch {
                // Private-mode storage refusal — the panel still shows the date.
            }
            await logout();
        } catch (err) {
            if (isOwnerMustTransfer(err)) {
                setOutcome({ kind: 'owner_must_transfer' });
            } else {
                setError(getErrorMessage(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (outcome.kind === 'scheduled') {
        return (
            <div
                role="status"
                style={{
                    display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
                    maxWidth: 520, padding: 'var(--space-5)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                }}
            >
                <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontWeight: 600, color: 'var(--ok)' }}>
                    <Clock size={18} aria-hidden="true" /> Deletion scheduled
                </p>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                    Your account will be deleted on <strong>{formatDeletionDate(outcome.at)}</strong>.
                    You have been signed out.
                </p>
                <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--color-text-muted)' }}>
                    Changed your mind? Sign in again before that date and choose
                    “Keep my account”. After it passes, this cannot be undone.
                </p>
            </div>
        );
    }

    if (outcome.kind === 'owner_must_transfer') {
        return (
            <div
                role="status"
                style={{
                    display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
                    maxWidth: 520, padding: 'var(--space-5)',
                    // Accent, not danger: nothing failed here.
                    border: '1px solid var(--accent)', borderRadius: 'var(--radius-md)',
                }}
            >
                <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontWeight: 600, color: 'var(--accent-text)' }}>
                    <Users size={18} aria-hidden="true" /> One step first
                </p>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                    You own a workspace that other people are still in. Deleting your
                    account would leave them without an owner.
                </p>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                    Transfer ownership to another member, or close the workspace, and
                    then come back here.
                </p>
                <div>
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => { setOutcome({ kind: 'idle' }); setConfirmation(''); }}
                    >
                        Back
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 520 }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontWeight: 600, color: 'var(--danger)' }}>
                    <AlertTriangle size={18} aria-hidden="true" /> Delete your account
                </p>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                    This removes your profile, your personal settings, and your API keys.
                </p>
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                    Deletion completes <strong>{DELETION_GRACE_DAYS} days</strong> from
                    when you confirm. Until then you can sign in as usual and cancel it —
                    after that it cannot be undone.
                </p>
            </div>

            <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" htmlFor="delete-confirmation">
                    Type <code>{DELETE_CONFIRMATION}</code> to confirm
                </label>
                <input
                    className="form-input"
                    id="delete-confirmation"
                    value={confirmation}
                    onChange={e => setConfirmation(e.target.value)}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                />
            </div>

            {error && <p className="form-error" style={{ margin: 0 }}>{error}</p>}

            <div>
                <Button type="submit" variant="danger" size="md" disabled={!canSubmit}>
                    {submitting ? 'Deleting…' : 'Delete my account'}
                </Button>
            </div>
        </form>
    );
}
